import { useTranslation } from 'react-i18next'
import { MyWorkspacesSection } from '@/components/home/my-workspaces-section'
import { NextActionsSection } from '@/components/home/next-actions-section'
import { RecentCompetitionsSection } from '@/components/home/recent-competitions-section'
import { useHomeOverview } from '@/features/home/use-home-overview'
import { useSession } from '@/features/session/use-session'

/**
 * `/home`'s core Acceptance Criterion (2026 redesign, supersedes the v3.1.2
 * UX-045 quick-action dashboard): a multi-role account — student in one
 * competition, admin in another, judge in a third, platform admin on top —
 * sees one aggregated "我的下一步" list without first picking which role it
 * is right now. This component is purely an orchestrator: `useHomeOverview()`
 * does every derivation (see `features/home/derive-home.ts`), the three
 * sections below only render the resulting view model. No `if (isAdmin)`
 * branching lives here or in any child — that would silently reintroduce
 * the single-persona dashboard this redesign replaces.
 *
 * The header deliberately never announces a role ("你是平台管理員" /
 * "學生工作台") — see the product brief's Header section — because doing so
 * would imply the account has only that one role.
 */
export function SignedInDashboard() {
  const { t } = useTranslation('home')
  const session = useSession()
  const overview = useHomeOverview()

  const displayName =
    session.data?.authenticated === true ? session.data.user?.displayName : undefined

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          {t('dashboard.welcome', { name: displayName ?? '' })}
        </h1>
        <p className="text-lg text-muted-foreground">{t('dashboard.welcomeSubtitle')}</p>
      </section>

      <NextActionsSection actions={overview.actions} />
      <MyWorkspacesSection workspaces={overview.workspaces} />
      <RecentCompetitionsSection competitions={overview.recent} />
    </div>
  )
}
